import React, { useCallback, useState } from "react";
import { differenceInSeconds } from "date-fns";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useScope } from "@state/scopeContext.lib";
import { useDownloadDocumentsMutation } from "@api/financialDocument.api";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { DownloadIcon } from "@icons";
import { useFinancialDocumentBulkSelection } from "../FinancialDocumentBulkSelection/useFinancialDocumentBulkSelection";
import { LAST_DOCUMENTS_DOWNLOAD_KEY } from "./lib";

const isPastDownloadCooldown = () => {
    const lastDownload = localStorage.getItem(LAST_DOCUMENTS_DOWNLOAD_KEY);
    const lastDownloadDate = lastDownload
        ? new Date(parseInt(lastDownload, 10))
        : null;
    return (
        !lastDownloadDate ||
        differenceInSeconds(new Date(), lastDownloadDate) > 15
    );
};

export const FinancialDocumentsDownload: React.FC = () => {
    const { toast } = useToaster();
    const { workspaceId } = useScope();
    const bulkSelection = useFinancialDocumentBulkSelection();
    const [isDownloadAllowed, setIsDownloadAllowed] = useState(
        isPastDownloadCooldown,
    );
    const download = useDownloadDocumentsMutation(() => {
        localStorage.setItem(
            LAST_DOCUMENTS_DOWNLOAD_KEY,
            Date.now().toString(),
        );
        setIsDownloadAllowed(false);
        toast(
            "Your file is processing. You'll receive an email when it's ready.",
        );
    });

    const handleDownload = useCallback(() => {
        download.mutate(
            bulkSelection.filtersSelection
                ? {
                      workspaceId: workspaceId ?? "",
                      filters: { ...bulkSelection.filtersSelection },
                  }
                : {
                      workspaceId: workspaceId ?? "",
                      financialDocumentIds:
                          bulkSelection.selected.length > 0
                              ? bulkSelection.selected.map((fd) => fd.id)
                              : undefined,
                  },
        );
    }, [bulkSelection, download, workspaceId]);

    return (
        <ButtonWithLoader
            variant="primary"
            size="md"
            onClick={handleDownload}
            loading={download.isPending}
            disabled={!isDownloadAllowed}
            tooltip={
                isDownloadAllowed
                    ? null
                    : "Your file is processing. You'll receive an email when it's ready."
            }
            iconOnly
            aria-label="Download documents"
            data-testid="financial-documents-download-button"
        >
            <DownloadIcon />
        </ButtonWithLoader>
    );
};
